import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { TenantAppContext } from "../contexts/TenantContext";
import { useTenantAppFirestore } from "../hooks";
import { isLocalHost } from "@inloco/shared/utils";


export const TenantAppProvider = ({ children }: { children: ReactNode }) => {
    const { t } = useTranslation();
    const [now] = useState(() => Date.now())
    const { app, loading, mapBlobUrl, isMapImageLoading, error: firestoreError, getTenantAppByDomain } = useTenantAppFirestore();

    // Retrieve tenant info
    useEffect(() => {
        const loadTenant = async () => {
            // Retrieve current domain
            const domain = window.location.hostname;

            const tenantSlug = isLocalHost()
                ? 'localhost'
                : domain.split('.')[0];

            await getTenantAppByDomain(tenantSlug);
        };

        loadTenant();
    }, [getTenantAppByDomain]);

    const value = useMemo(() => ({
        app,
        mapBlobUrl,
    }), [app, mapBlobUrl]);

    const error = useMemo(() => {
        if (!app || !now) return null;
        const { subscription } = app;

        // Check if subscription is expired
        if (subscription.status === 'expired') {
            return 'errors.subscriptionExpired';
        }

        // Check if subscription is suspended
        if (subscription.status === 'suspended') {
            return 'errors.subscriptionSuspended';
        }

        return null;
    }, [app, now]);

    // Show loader until tenant is loaded
    if (loading && isMapImageLoading && !app) {
        return (
            <div className="flex flex-col justify-center items-center h-screen gap-4">
                <span className="loading loading-dots loading-xl"></span>
                <p className="text-xl">{t('loading.tenant')}</p>
            </div>
        );
    }

    // If there is no tenant and there is an error, show an error message
    if (firestoreError) {
        return (
            <div className="flex flex-col justify-center items-center h-screen p-8 text-center gap-4">
                <h1 className="text-2xl font-bold">{t('errors.tenantErrorTitle')}</h1>
                <p>{t('errors.tenantError')}</p>
            </div>
        );
    }

    // If there is a subscription error (trial, expired or suspended)
    if (error) {
        let titleKey = 'errors.subscriptionSuspendedTitle';

        if (error === 'errors.subscriptionExpired') {
            titleKey = 'errors.subscriptionExpiredTitle';
        }

        return (
            <div className="flex flex-col justify-center items-center h-screen p-8 text-center gap-4">
                <h1 className="text-2xl font-bold">{t(titleKey)}</h1>
                <p>{t(error)}</p>
            </div>
        );
    }

    return (
        <TenantAppContext.Provider value={value}>
            {children}
        </TenantAppContext.Provider>
    );
};
